/** @typedef {import('../types/anomalyTimeSeries.types').TimeSeriesDataPoint} TimeSeriesDataPoint */
/** @typedef {import('../types/anomalyTimeSeries.types').TimeSeriesSummary} TimeSeriesSummary */

const SEVERITY_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

/**
 * Build summary stats from a slice of data points (e.g. range zoom).
 * @param {TimeSeriesDataPoint[]} dataPoints
 * @returns {TimeSeriesSummary}
 */
export const deriveTimeSeriesSummaryFromDataPoints = (dataPoints) => {
  if (!dataPoints?.length) {
    return { totalAnomalies: 0, periodsWithAnomalies: 0, maxSeverity: null };
  }

  let totalAnomalies = 0;
  let periodsWithAnomalies = 0;
  let maxSeverity = null;
  let maxRank = 0;

  dataPoints.forEach((dp) => {
    if (!dp.hasAnomaly) return;
    periodsWithAnomalies += 1;
    totalAnomalies += dp.anomalyCount ?? 0;
    const rank = SEVERITY_RANK[dp.maxSeverity] || 0;
    if (rank > maxRank) {
      maxRank = rank;
      maxSeverity = dp.maxSeverity;
    }
  });

  return { totalAnomalies, periodsWithAnomalies, maxSeverity };
};

/**
 * @param {TimeSeriesSummary|null} summary
 * @param {TimeSeriesDataPoint[]} dataPoints
 * @returns {'STABLE'|'WATCH'|'AT_RISK'|'CRITICAL'}
 */
export const deriveHealthStatus = (summary, dataPoints) => {
  const total = summary?.totalAnomalies ?? 0;
  if (total === 0) return "STABLE";

  const maxSev = summary?.maxSeverity;
  if (maxSev === "CRITICAL") return "CRITICAL";
  if (maxSev === "HIGH") return "AT_RISK";

  const hasHighOrCritical = dataPoints.some(
    (dp) => dp.hasAnomaly && (dp.maxSeverity === "HIGH" || dp.maxSeverity === "CRITICAL")
  );
  if (hasHighOrCritical) return "AT_RISK";

  return "WATCH";
};

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 * @param {boolean} unhealthy
 */
const longestStreak = (dataPoints, unhealthy) => {
  let best = 0;
  let current = 0;
  dataPoints.forEach((dp) => {
    const match = unhealthy ? dp.hasAnomaly : !dp.hasAnomaly;
    if (match) {
      current += 1;
      best = Math.max(best, current);
    } else {
      current = 0;
    }
  });
  return best;
};

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 */
export const deriveStreaks = (dataPoints) => ({
  longestClean: longestStreak(dataPoints, false),
  longestUnhealthy: longestStreak(dataPoints, true),
});

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 */
export const deriveLatestPeriodStatus = (dataPoints) => {
  if (!dataPoints.length) return null;
  const last = dataPoints[dataPoints.length - 1];
  return {
    period: last.period,
    value: last.value,
    hasAnomaly: last.hasAnomaly,
    maxSeverity: last.maxSeverity,
    maxDeviationPct: last.maxDeviationPct,
  };
};

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 */
export const hasMissingValueAnomalies = (dataPoints) =>
  dataPoints.some((dp) => dp.hasAnomaly && dp.value == null);

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 */
export const hasOnlyMissingValueAnomalies = (dataPoints) => {
  const anomalyPoints = dataPoints.filter((dp) => dp.hasAnomaly);
  return anomalyPoints.length > 0 && anomalyPoints.every((dp) => dp.value == null);
};

export { SEVERITY_RANK };
